import React, {useEffect} from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import DropdownField, {DropdownOption} from "../../formFields/DropdownField";
import '../CustomTable.scss';
import TextField from '../../formFields/TextField';
import DateField from '../../formFields/DateField';
import FormConstants from '../../../components/dataEntryDetails/dataEntryComponents/DataEntryFieldsConstants';
import {connect} from "react-redux";
import TransactionDocument, { ModifiableValue } from '../../../models/document';
import FeeSchedule from '../../../models/feeSchedule';
import {TransactionDetails} from '../../../models/transaction';
import {DocTypes} from '../../constants/DocTypes';
import { AdministrationLevel } from '../../../models/misc';

interface Data {
    description: string;
    source: string;
    sab: string;
    size: string;
    pageCount: number;
    actions: string;
    dateOfService: string;
}

export class MedDocumentErrors {
    constructor(from: string, to: string, account: string, state: string, feeSchedule: string) {
        this.from = from;
        this.to = to;
        this.account = account;
        this.state = state;
        this.feeSchedule = feeSchedule;
    }

    from: string;
    to: string;
    account: string;
    state: string;
    feeSchedule: string;

    isValid() {
        return !this.to && !this.from && !this.account && !this.state && !this.feeSchedule;
    }
}

type Order = 'asc' | 'desc';

interface HeadCell {
    id: keyof Data;
    label: string;
    sort: boolean;
}

const headCells: HeadCell[] = [
    {id: 'description', label: 'RECORD NAME', sort: true},
    {id: 'sab', label: 'S/A/B', sort: true},
    {id: 'size', label: 'SIZE', sort: true},
    {id: 'pageCount', label: 'PAGES', sort: true},
    {id: 'actions', label: 'ACTIONS', sort: false},
];

interface EnhancedTableProps {
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    order: Order;
    orderBy: string;
    hideSource?: boolean;
    updatedRecords?: readonly TransactionDocument[];
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const {order, orderBy, onRequestSort} = props;
    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };
    let updatedHeadCells = [...headCells]
    if (props.hideSource) {
        updatedHeadCells.splice(1, 1)
    }
    return (
        <TableHead>
            <TableRow>
                {updatedHeadCells.map((headCell) => (
                    <TableCell colSpan={4}
                               key={headCell.id}
                               sortDirection={orderBy === headCell.id ? order : false}
                    >
                        {headCell.sort ?
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {headCell.label} {headCell.id === 'pageCount' && props.updatedRecords ?
                                '(' + props.updatedRecords
                                    .filter(record => record.type?.role === DocTypes.MED)
                                    .reduce((pageCount, record) => pageCount + record.pageCount, 0) +
                                ')' : ''
                            }
                            </TableSortLabel> :
                            headCell.label
                        }
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

interface IpropTypes {
    rows: readonly TransactionDocument[],
    getDocumentById: (documentId: string, auditOnShowDoc: boolean) => void,
    setShowRecordGrid: (value: boolean) => void,
    hideSource?: boolean,
    setSelectedMedDocumentIndex: (id: string) => void,
    transactionId: string,
    transactionDetails?: TransactionDetails,
    documentSources: readonly DropdownOption[],
    deleteDocument: (id: string) => void,
    getTransactionDocuments: (transactionId: string) => void,
    deleteDocumentSuccess?: boolean,
    minDate: Date,
    maxDate: Date,
    auditOnShowDoc: boolean,
    feeSchedules?: readonly FeeSchedule[],
    isEditing: boolean,
    changeSubRowData: (documentId: string, value: string, key: ModifiableValue) => void,
    errors: readonly MedDocumentErrors[],
    isMultiState: boolean,
    billingStates: readonly AdministrationLevel[],
}

function MedicalRecordsRetrieve(props: IpropTypes) {
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('pageCount');

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const removeDocument = (id: string) => {
        if (window.confirm("Are you certain you wish to delete this file?")) {
            props.deleteDocument(id)
        }
    }

    useEffect(() => {
        if (props.deleteDocumentSuccess)
            props.getTransactionDocuments(props.transactionId)
    }, [props.deleteDocumentSuccess]);

    const medAPIFetch = (documentId: string) => {
        props.setShowRecordGrid(false)
        props.setSelectedMedDocumentIndex(documentId)
        props.getDocumentById(documentId, props.auditOnShowDoc)
    }

    return (
        <>
            <div className="records-table-main" style={props.hideSource ? {width: "85%"} : {}}>
                <Table
                    size="small" stickyHeader className='records-table'
                >
                    <div className="records-table-inner">
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            hideSource={props.hideSource}
                            updatedRecords={props.rows}
                        />
                        <TableBody>
                            {props.rows.map((row, index) => <>
                                <TableRow className="edit-record-row">
                                    <TableCell className="record_name" colSpan={4}>
                                        {props.isEditing ? <TextField
                                            className="edit-textinput-medical"
                                            label="" value={row.description}
                                            onChange={(value) => {
                                                props.changeSubRowData(row.id, value, ModifiableValue.Description)
                                            }}
                                            errMsg=""
                                            placeholder=""
                                            suppressEnterSubmit={true}
                                        /> : <a
                                            href="#!"
                                            onClick={() => medAPIFetch(row.id)}
                                            style={row.description ? {} : {opacity: 0.5}}
                                        >
                                            {row.description || "No file name"}
                                        </a>}
                                    </TableCell>
                                    <TableCell className="record_sab" colSpan={4}>{row.sab}</TableCell>
                                    <TableCell className="record_size" colSpan={4}>{row.size ? (row.size / (1024 * 1024)).toFixed(2) + " MB" : ""}</TableCell>
                                    <TableCell className="record_pages"
                                                colSpan={4}>{row.pageCount ?? 0} {row.pageCount != 1 ? "pages" : "page"}</TableCell>
                                    <TableCell className="record_options" colSpan={4}>
                                        <div className="record_options_items">
                                            <span className="remove_doc" onClick={() => removeDocument(row.id)}
                                                    style={{display: 'flex', alignItems: 'center', marginTop: 1}}>
                                                <svg width="15" height="15" viewBox="0 0 1200 1200" fill="none"
                                                        xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M600 0C268.629 0 0 268.629 0 600C0 931.371 268.629 1200 600 1200C931.371 1200 1200 931.371 1200 600C1200 268.629 931.371 0 600 0ZM411.475 262.5L600 451.025L788.525 262.5L937.5 411.475L748.975 600L937.5 788.525L788.525 937.5L600 748.975L411.475 937.5L262.5 788.525L451.025 600L262.5 411.475L411.475 262.5Z"
                                                        fill="#CC5967"/>
                                                </svg>
                                                <span className='record_remove_span'>Remove</span>
                                            </span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                {props.isEditing && <>
                                    <TableRow className="secondrow">
                                        <TableCell colSpan={5} style={{width: "25%"}}>
                                            <DateField
                                                label={FormConstants.MEDICAL_RECORDS_FROM}
                                                name={FormConstants.MEDICAL_RECORDS_FROM}
                                                minDate={props.minDate}
                                                maxDate={row.dateToService ?? props.maxDate}
                                                placeholder="mm/dd/yyyy"
                                                errMsg={props.errors[index].from}
                                                value={row.dateOfService}
                                                onChange={(value) => {
                                                    props.changeSubRowData(row.id, value?.toString() ?? "", ModifiableValue.From)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell colSpan={5} style={{width: "25%"}}>
                                            <DateField
                                                label={FormConstants.MEDICAL_RECORDS_TO}
                                                name={FormConstants.MEDICAL_RECORDS_TO}
                                                minDate={row.dateOfService ?? props.minDate}
                                                maxDate={props.maxDate}
                                                placeholder="mm/dd/yyyy"
                                                errMsg={props.errors[index].to}
                                                value={row.dateToService}
                                                onChange={(value) => {
                                                    props.changeSubRowData(row.id, value?.toString() ?? "", ModifiableValue.To)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell colSpan={5} style={{width: "25%"}}>
                                            {props.isMultiState && <DropdownField
                                                label="State"
                                                options={[
                                                    {label: "None", value: "", disabled: true, hidden: true},
                                                    ...props.billingStates.map((state: any) => {
                                                        return {label: state.name, value: state.abbreviation}
                                                    })
                                                ]}
                                                onChange={event => {
                                                    props.changeSubRowData(row.id, event.target.value, ModifiableValue.State);
                                                }}
                                                errMsg={props.errors[index].state}
                                                value={row.state ?? ""}
                                                required={true}
                                                meta="state"
                                                key={row.id + row.state + row.feeScheduleId}
                                            />}
                                        </TableCell>
                                        <TableCell colSpan={5} style={{width: "25%"}}>
                                            {props.isMultiState && <DropdownField
                                                label="Fee Schedule"
                                                required={true}
                                                options={[
                                                    {label: "- Select -", value: "", disabled: true, hidden: true},
                                                    ...props.feeSchedules!.filter(fee => {
                                                        return !fee.state || fee.state == row.state
                                                    }).map(fee => {
                                                        return {label: fee.name, value: fee.id}
                                                    }),
                                                ]}
                                                onChange={event => {
                                                    props.changeSubRowData(row.id, event.target.value, ModifiableValue.FeeSchedule);
                                                }}
                                                errMsg={props.errors[index].feeSchedule}
                                                value={row.feeScheduleId ?? ""}
                                                meta="feeScheduleId"
                                                key={row.id + row.state + row.feeScheduleId}
                                            />}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow key={`medret2${row[index]}`} className="secondrow">
                                        <TableCell colSpan={5} style={{width: "25%"}}>
                                            <DropdownField
                                                label="Source"
                                                required={true}
                                                options={props.documentSources}
                                                onChange={(event) => { props.changeSubRowData(row.id, event.target.value, ModifiableValue.Source) }}
                                                input={{ value: row.sourceID?.toString() || "" }}
                                                meta="abc"
                                            />
                                        </TableCell>
                                        <TableCell className="account-no-text" colSpan={5} style={{width: "25%"}}>
                                            <TextField
                                                label={FormConstants.MEDICAL_ACCOUNT_NUMBER}
                                                name={FormConstants.MEDICAL_ACCOUNT_NUMBER}
                                                maxLength={100}
                                                placeholder="123456789"
                                                errMsg={props.errors[index].account}
                                                disabled={false}
                                                onChange={(value) => {
                                                    props.changeSubRowData(row.id, value, ModifiableValue.Account)
                                                }}
                                                value={row.account}
                                                suppressEnterSubmit={true}
                                            />
                                        </TableCell>
                                        <TableCell colSpan={5} style={{width: "25%"}}></TableCell>
                                        <TableCell colSpan={5} style={{width: "25%"}}></TableCell>
                                    </TableRow>
                                </>}
                            </>)}
                        </TableBody>
                    </div>
                </Table>
            </div>
        </>
    );
}

function mapStateToProps(state: any) {
    return {
        deleteDocumentSuccess: state.dataEntryState.deleteDocumentAPISuccess,
        transactionDetails: state.dataEntryState.transactionDetails,
        feeSchedules: state.dataEntryState.feeSchedules,
    };
}

export function mapDispatchToProps(dispatch: Function) {
    return {
    };
}

export default connect<{}, {}, IpropTypes>(
    mapStateToProps,
    mapDispatchToProps
)(MedicalRecordsRetrieve);
